/**
 * @fileoverview Assignment Preview Dialog Component
 * 
 * Dialog for previewing and confirming batch assignment operations.
 * Shows detailed preview of what will be assigned/unassigned and potential conflicts.
 * 
 * Requirements: 4.4, 4.5
 */

import React, { useState, useEffect, useMemo } from 'react';
import { ASSIGNMENT_STATUS_CONFIG } from '../types.js';

/**
 * Assignment Preview Dialog Component
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether dialog is open
 * @param {string} props.operation - Operation type ('assign', 'unassign', 'reassign')
 * @param {Array} props.files - Files involved in operation
 * @param {Array} props.parrots - Parrots involved in operation
 * @param {Object} props.currentAssignments - Current assignments (fileId -> assignments[])
 * @param {Array} props.conflicts - Detected conflicts
 * @param {boolean} props.loading - Whether preview is loading
 * @param {function} props.onConfirm - Confirm handler
 * @param {function} props.onCancel - Cancel handler
 * @param {string} [props.className] - CSS class name
 */
export const AssignmentPreviewDialog = ({
  isOpen,
  operation,
  files,
  parrots,
  currentAssignments,
  conflicts = [],
  loading = false,
  onConfirm,
  onCancel,
  className=''
}) => {
  const [showDetails, setShowDetails] = useState(false);

  // Calculate operation summary
  const operationSummary = useMemo(() => {
    const totalOperations = files.length * parrots.length;
    let newAssignments = 0;
    let removedAssignments = 0;
    let unchangedAssignments = 0;

    files.forEach(file => {
      parrots.forEach(parrot => {
        const currentlyAssigned = currentAssignments[file.id]?.some(
          a => a.parrotId === parrot.id && a.status !== 'removed'
        );

        if (operation === 'assign') {
          if (!currentlyAssigned) {
            newAssignments++;
          } else {
            unchangedAssignments++;
          }
        } else if (operation === 'unassign') {
          if (currentlyAssigned) {
            removedAssignments++;
          } else {
            unchangedAssignments++;
          }
        } else if (operation === 'reassign') {
          if (!currentlyAssigned) {
            newAssignments++;
          } else {
            unchangedAssignments++;
          }
        }
      });
    });

    return {
      totalOperations,
      newAssignments,
      removedAssignments,
      unchangedAssignments,
      hasChanges: newAssignments > 0 || removedAssignments > 0
    };
  }, [files, parrots, currentAssignments, operation]);

  // Get operation details for each file-parrot combination
  const operationDetails = useMemo(() => {
    const details = [];

    files.forEach(file => {
      parrots.forEach(parrot => {
        const currentlyAssigned = currentAssignments[file.id]?.some(
          a => a.parrotId === parrot.id && a.status !== 'removed'
        );

        let action = 'no-change';
        let status = 'unchanged';

        if (operation === 'assign') {
          if (!currentlyAssigned) {
            action = 'assign';
            status = 'new';
          }
        } else if (operation === 'unassign') {
          if (currentlyAssigned) {
            action = 'unassign';
            status = 'removed';
          }
        } else if (operation === 'reassign') {
          if (!currentlyAssigned) {
            action = 'assign';
            status = 'new';
          }
        }

        if (action !== 'no-change') {
          details.push({
            fileId: file.id,
            fileName: file.name,
            parrotId: parrot.id,
            parrotName: parrot.name,
            action,
            status,
            currentlyAssigned
          });
        }
      });
    });

    return details;
  }, [files, parrots, currentAssignments, operation]);

  // Get operation title and description
  const getOperationInfo = () => {
    switch (operation) {
      case 'assign':
        return {
          title: 'Batch Assign Files',
          description: 'Assign selected files to selected parrots',
          icon: '📎',
          color: 'teal'
        };
      case 'unassign':
        return {
          title: 'Batch Remove Files',
          description: 'Remove selected files from selected parrots',
          icon: '🗑️',
          color: 'red'
        };
      case 'reassign':
        return {
          title: 'Batch Reassign Files',
          description: 'Reassign selected files to selected parrots (removes from others)',
          icon: '🔄',
          color: 'blue'
        };
      default:
        return {
          title: 'Batch Operation',
          description: 'Perform batch operation',
          icon: '⚙️',
          color: 'gray'
        };
    }
  };

  const operationInfo = getOperationInfo();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75"
          onClick={onCancel}
        />

        {/* Dialog */}
        <div className={`inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-2xl ${className}`}>
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${
                operationInfo.color === 'teal' ? 'bg-teal-100 dark:bg-teal-900/30' :
                operationInfo.color === 'red' ? 'bg-red-100 dark:bg-red-900/30' :
                operationInfo.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/30' :
                'bg-gray-100 dark:bg-gray-700'
              }`}>
                <span className="text-2xl">{operationInfo.icon}</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {operationInfo.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
                  {operationInfo.description}
                </p>
              </div>
            </div>
            
            <button
              onClick={onCancel}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors dark:text-gray-300 dark:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Loading state */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
              <span className="ml-3 text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">Analyzing operation...</span>
            </div>
          )}

          {/* Content */}
          {!loading && (
            <>
              {/* Operation summary */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6 dark:bg-gray-900">
                <h4 className="font-medium text-gray-900 dark:text-white mb-3">Operation Summary</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {files.length}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
                      File{files.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {parrots.length}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
                      Parrot{parrots.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-teal-600 dark:text-teal-400">
                      {operationSummary.newAssignments}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
                      New Assignment{operationSummary.newAssignments !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                      {operationSummary.removedAssignments}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
                      Removal{operationSummary.removedAssignments !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              </div>

              {/* Conflicts warning */}
              {conflicts.length > 0 && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <svg className="h-6 w-6 text-yellow-600 dark:text-yellow-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <div>
                      <h4 className="font-medium text-yellow-800 dark:text-yellow-200 mb-1">
                        {conflicts.length} Conflict{conflicts.length !== 1 ? 's' : ''} Detected
                      </h4>
                      <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-2">
                        The following conflicts were detected and will need to be resolved:
                      </p>
                      <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                        {conflicts.slice(0, 3).map((conflict, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <span className="text-yellow-600 dark:text-yellow-400 mt-0.5">•</span>
                            <span>{conflict.message}</span>
                          </li>
                        ))}
                        {conflicts.length > 3 && (
                          <li className="text-yellow-600 dark:text-yellow-400">
                            ... and {conflicts.length - 3} more
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* No changes warning */}
              {!operationSummary.hasChanges && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                  <div className="flex items-center space-x-3">
                    <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <h4 className="font-medium text-blue-800 dark:text-blue-200">No Changes Required</h4>
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        All selected files are already in the desired state for the selected parrots.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Operation details */}
              {operationDetails.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      Operation Details ({operationDetails.length} changes)
                    </h4>
                    <button
                      onClick={() => setShowDetails(!showDetails)}
                      className="text-sm text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 transition-colors"
                    >
                      {showDetails ? 'Hide Details' : 'Show Details'}
                    </button>
                  </div>

                  {showDetails && (
                    <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg dark:border-gray-700">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 dark:bg-gray-900">
                          <tr>
                            <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600">File</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600">Parrot</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600">Action</th>
                            <th className="px-4 py-2 text-left font-medium text-gray-700 dark:text-gray-300 dark:text-gray-600">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                          {operationDetails.map((detail, index) => (
                            <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-900">
                              <td className="px-4 py-2 text-gray-900 dark:text-white truncate max-w-xs">
                                {detail.fileName}
                              </td>
                              <td className="px-4 py-2 text-gray-900 dark:text-white truncate max-w-xs">
                                {detail.parrotName}
                              </td>
                              <td className="px-4 py-2">
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  detail.action === 'assign' 
                                    ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200'
                                    : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
                                }`}>
                                  {detail.action === 'assign' ? '📎 Assign' : '🗑️ Remove'}
                                </span>
                              </td>
                              <td className="px-4 py-2">
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  detail.status === 'new'
                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                                }`}>
                                  {detail.status === 'new' ? '✨ New' : '🗑️ Removed'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Action buttons */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={onCancel}
                  className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors dark:bg-gray-800 dark:bg-gray-900 dark:text-gray-600"
                >
                  Cancel
                </button>
                
                <button
                  onClick={onConfirm}
                  disabled={!operationSummary.hasChanges}
                  className={`px-4 py-2 text-sm text-white rounded-lg transition-colors ${
                    operationInfo.color === 'teal' ? 'bg-teal-600 hover:bg-teal-700' :
                    operationInfo.color === 'red' ? 'bg-red-600 hover:bg-red-700' :
                    operationInfo.color === 'blue' ? 'bg-blue-600 hover:bg-blue-700' :
                    'bg-gray-600 hover:bg-gray-700'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {conflicts.length > 0 ? 'Proceed with Conflicts' : 'Confirm Operation'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
